/* eslint-disable */

// components/AxisCalculator.jsx

import React, { useEffect, useState } from "react";
import { Card, Grid, TextField, Typography } from "@mui/material";

/**
 * AxisCalculator
 *
 * Props:
 *  - initialValues: optional object with initial values for inputs and computed fields
 *  - onValuesChange: function(payload) called whenever computed values change
 *
 * Behavior:
 *  - Uses approximate formula Axis ≈ atan2(aVF, LeadI) * (180/π)
 *  - Uses provided formatAxis() to present axis as integer (no decimals) or 1 decimal
 *  - Also exposes numeric degrees (one decimal) in payload for programmatic use
 *  - Interpretation rules:
 *      QRS normal: -30° to +90°
 *      P normal: 0° to +75°
 *      T normal: within ±45° of QRS axis
 */

/* ---------------- user-provided formatting/calculation (exact) ---------------- */
function formatAxis(value) {
  let rounded = Math.round(value * 10) / 10; // round to 1 decimal
  return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1);
}

function calculateApproxAxis(aVF, leadI) {
  const x = Number(leadI);
  const y = Number(aVF);

  if (!Number.isFinite(x) || !Number.isFinite(y)) return "";
  if (x === 0 && y === 0) return "Indeterminate";

  // atan2 handles quadrant correction; returns radians
  const angle = Math.atan2(y, x) * (180 / Math.PI);

  // normalize to -180 .. +180 (atan2 already does this, but keep defensive)
  let normalized = angle;
  if (normalized > 180) normalized -= 360;
  if (normalized <= -180) normalized += 360;

  return formatAxis(normalized);
}
/* ----------------------------------------------------------------------------- */

/* helper: compute numeric degrees (float) or null if indeterminate/invalid */
function calculateNumericAngle(aVF, leadI) {
  const x = Number(leadI);
  const y = Number(aVF);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (x === 0 && y === 0) return null;
  let angleDeg = Math.atan2(y, x) * (180 / Math.PI);
  if (angleDeg > 180) angleDeg -= 360;
  if (angleDeg <= -180) angleDeg += 360;
  return angleDeg;
}

/* interpretation helpers */
function interpretQRS(angleDeg) {
  if (angleDeg === null) return "Indeterminate";
  if (angleDeg >= -30 && angleDeg <= 90) return "Normal";
  if (angleDeg < -30 && angleDeg >= -90) return "Left Axis Deviation";
  if (angleDeg > 90 && angleDeg <= 180) return "Right Axis Deviation";
  return "Extreme Axis";
}

function interpretP(angleDeg) {
  if (angleDeg === null) return "Indeterminate";
  if (angleDeg >= 0 && angleDeg <= 75) return "Normal";
  return "Abnormal";
}

function interpretT(angleDeg, qrsAngleDeg) {
  if (angleDeg === null) return "Indeterminate";
  if (qrsAngleDeg === null) return "Unknown (QRS missing)";
  // within ±45° of QRS axis
  return Math.abs(angleDeg - qrsAngleDeg) <= 45 ? "Normal" : "Abnormal";
}

/* ------------------------- Component ------------------------- */
export default function AxisCalculator({ initialValues = {}, onValuesChange = () => { } }) {
  const [state, setState] = useState({
    // raw inputs (can be "" or numeric or "-" while typing)
    pp_box: initialValues.pp_interval_box ?? "",
    p_avf_box: initialValues.p_avf_box ?? "",
    p_leadi_box: initialValues.p_leadi_box ?? "",
    qrs_avf_box: initialValues.qrs_avf_box ?? "",
    qrs_leadi_box: initialValues.qrs_leadi_box ?? "",
    t_avf_box: initialValues.t_avf_box ?? "",
    t_leadi_box: initialValues.t_leadi_box ?? "",
    // computed/display (formatted strings)
    pp_ms: initialValues.pp_interval ?? "",
    P_Axis: initialValues.PAxis ?? "",
    P_Axis_Interpretation: initialValues.PAxisInterpretation ?? "",
    QRS_Axis: initialValues.QRSAxis ?? "",
    QRS_Axis_Interpretation: initialValues.QRSAxisInterpretation ?? "",
    T_Axis: initialValues.TAxis ?? "",
    T_Axis_Interpretation: initialValues.TAxisInterpretation ?? "",
  });

  useEffect(() => {
    // PP interval conversion: 1 box = 40 ms
    const pp_ms =
      state.pp_box === ""
        ? ""
        : (() => {
          const n = Number(state.pp_box);
          return Number.isFinite(n) ? String(Math.round(n * 40)) : "";
        })();

    // P axis (formatted + numeric)
    const PAxisFormatted =
      state.p_avf_box === "" || state.p_leadi_box === "" ? "" : calculateApproxAxis(state.p_avf_box, state.p_leadi_box);
    const PAxisNumeric = calculateNumericAngle(state.p_avf_box, state.p_leadi_box);
    const PAxisInterp = interpretP(PAxisNumeric);

    // QRS axis
    const QRSAxisFormatted =
      state.qrs_avf_box === "" || state.qrs_leadi_box === "" ? "" : calculateApproxAxis(state.qrs_avf_box, state.qrs_leadi_box);
    const QRSAxisNumeric = calculateNumericAngle(state.qrs_avf_box, state.qrs_leadi_box);
    const QRSAxisInterp = interpretQRS(QRSAxisNumeric);

    // T axis
    const TAxisFormatted =
      state.t_avf_box === "" || state.t_leadi_box === "" ? "" : calculateApproxAxis(state.t_avf_box, state.t_leadi_box);
    const TAxisNumeric = calculateNumericAngle(state.t_avf_box, state.t_leadi_box);
    const TAxisInterp = interpretT(TAxisNumeric, QRSAxisNumeric);

    // Prepare payload
    const payload = {
      // raw inputs
      pp_interval_box: state.pp_box,
      p_avf_box: state.p_avf_box,
      p_leadi_box: state.p_leadi_box,
      qrs_avf_box: state.qrs_avf_box,
      qrs_leadi_box: state.qrs_leadi_box,
      t_avf_box: state.t_avf_box,
      t_leadi_box: state.t_leadi_box,
      // converted PP interval (ms)
      pp_interval: pp_ms,
      // formatted axis strings (using formatAxis)
      PAxis: PAxisFormatted,
      QRSAxis: QRSAxisFormatted,
      TAxis: TAxisFormatted,
      // numeric degrees (one decimal) for programmatic use ("" if indeterminate)
      PAxisDegrees: PAxisNumeric === null ? "" : Number(PAxisNumeric.toFixed(1)),
      QRSAxisDegrees: QRSAxisNumeric === null ? "" : Number(QRSAxisNumeric.toFixed(1)),
      TAxisDegrees: TAxisNumeric === null ? "" : Number(TAxisNumeric.toFixed(1)),
      // interpretations
      PAxisInterpretation: PAxisInterp,
      QRSAxisInterpretation: QRSAxisInterp,
      TAxisInterpretation: TAxisInterp,
    };

    // call parent callback
    onValuesChange(payload);

    // update local display fields
    setState((s) => ({
      ...s,
      pp_ms,
      P_Axis: payload.PAxis,
      P_Axis_Interpretation: payload.PAxisInterpretation,
      QRS_Axis: payload.QRSAxis,
      QRS_Axis_Interpretation: payload.QRSAxisInterpretation,
      T_Axis: payload.TAxis,
      T_Axis_Interpretation: payload.TAxisInterpretation,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.pp_box,
    state.p_avf_box,
    state.p_leadi_box,
    state.qrs_avf_box,
    state.qrs_leadi_box,
    state.t_avf_box,
    state.t_leadi_box,
  ]);

  // allow empty, "-" or numeric while typing
 const handleBoxChange = (key) => (e) => {
    const raw = e.target.value;
    // allow empty and standalone +/-
    if (raw === "" || raw === "-" || raw === "+") {
      setState((s) => ({ ...s, [key]: raw }));
      return;
    }
    // allow decimal numbers like "1", "1.", "1.5", ".5", "-0.5", "+2.3"
    const decimalPattern = /^[-+]?\d*\.?\d*$/;
    if (decimalPattern.test(raw)) {
      setState((s) => ({ ...s, [key]: raw }));
    }
  };


  const fmt = (v) => (v === "" || v === null || v === undefined ? "—" : String(v));

  return (
    <Card sx={{ width: "100%", p: 1 }}>
      <Grid container spacing={2} alignItems="center">
        {/* PP Interval */}
        <Grid item xs={12}>
          <Typography variant="caption">PP Interval*</Typography>
          <TextField
            name="pp_interval_box"
            value={state.pp_box}
            onChange={handleBoxChange("pp_box")}
            placeholder=""
            fullWidth
            size="small"
            InputProps={{
              endAdornment: <span style={{ color: "#555", marginRight: 8 }}>boxes</span>,
            }}
          />
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            ms = <b>{state.pp_ms || "—"}</b> (box × 40)
          </Typography>
        </Grid>

        {/* P wave */}
        <Grid item xs={6}>
          <Typography variant="caption">P wave amplitude (aVF)*</Typography>
          <TextField
            fullWidth
            size="small"
            value={state.p_avf_box}
            onChange={handleBoxChange("p_avf_box")}
            InputProps={{
              endAdornment: <span style={{ color: "#555", marginRight: 8 }}>boxes</span>,
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption">P wave amplitude (Lead I)*</Typography>
          <TextField
            fullWidth
            size="small"
            value={state.p_leadi_box}
            onChange={handleBoxChange("p_leadi_box")}
            InputProps={{
              endAdornment: <span style={{ color: "#555", marginRight: 8 }}>boxes</span>,
            }}
          />
        </Grid>

        {/* QRS */}
        <Grid item xs={6}>
          <Typography variant="caption">QRS net amplitude (aVF)*</Typography>
          <TextField
            fullWidth
            size="small"
            value={state.qrs_avf_box}
            onChange={handleBoxChange("qrs_avf_box")}
            InputProps={{
              endAdornment: <span style={{ color: "#555", marginRight: 8 }}>boxes</span>,
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption">QRS net amplitude (Lead I)*</Typography>
          <TextField
            fullWidth
            size="small"
            value={state.qrs_leadi_box}
            onChange={handleBoxChange("qrs_leadi_box")}
            InputProps={{
              endAdornment: <span style={{ color: "#555", marginRight: 8 }}>boxes</span>,
            }}
          />
        </Grid>

        {/* T wave */}
        <Grid item xs={6}>
          <Typography variant="caption">T wave amplitude (aVF)*</Typography>
          <TextField
            fullWidth
            size="small"
            value={state.t_avf_box}
            onChange={handleBoxChange("t_avf_box")}
            InputProps={{
              endAdornment: <span style={{ color: "#555", marginRight: 8 }}>boxes</span>,
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption">T wave amplitude (Lead I)*</Typography>
          <TextField
            fullWidth
            size="small"
            value={state.t_leadi_box}
            onChange={handleBoxChange("t_leadi_box")}
            InputProps={{
              endAdornment: <span style={{ color: "#555", marginRight: 8 }}>boxes</span>,
            }}
          />
        </Grid>

        {/* Results */}
        <Grid item xs={12} mt={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            Calculated P-R-T Axis :
            &nbsp; {fmt(state.P_Axis)}° &nbsp;—&nbsp; {fmt(state.QRS_Axis)}° &nbsp;—&nbsp; {fmt(state.T_Axis)}°
          </Typography>

          <Typography variant="body2" sx={{ mt: 0.5 }}>
            P: {fmt(state.P_Axis_Interpretation)} &nbsp; | &nbsp; QRS: {fmt(state.QRS_Axis_Interpretation)} &nbsp; | &nbsp; T:{" "}
            {fmt(state.T_Axis_Interpretation)}
          </Typography>
        </Grid>
      </Grid>
    </Card>
  );
}
